// GET /admin/organizations — paginated list + platform-wide totals.
export interface OrgSummary {
  id: string;
  name: string;
  type: string;
  status: string;
  wallet_balance: number;
  last_activity: string | null;
  created_at: string;
}

export interface OrganizationsListResponse {
  status: boolean;
  page: number;
  limit: number;
  total_pages: number;
  pages?: number;
  total_org_count: number;
  active_org_count: number;
  total_wallet_balance: number;
  organizations: OrgSummary[];
}

// GET /admin/organizations/{id}
export interface OrgTransaction {
  id: number;
  client_id_customer_id?: string;
  customer_id?: string;
  balance?: string;
  comment?: string;
  transaction_id: string;
  amount: string;
  date_key?: string;
  transaction_type: "credit" | "debit" | string;
  source?: string;
  currency: string;
  source_metadata?: Record<string, unknown>;
  payment_reference_number?: string;
  created_at: string;
}

export interface OrgCampaign {
  campaign_id: string;
  campaign_name: string;
  channel: string;
  status: string;
  sender_name?: string;
  campaign_size: number;
  estimated_cost: number;
  created_at: string;
  scheduled_start?: string | null;
  analytics?: {
    attempted: number;
    sent: number;
    delivered: number;
    undelivered: number;
    failed: number;
    dnd: number;
    total_clicks: number;
    unique_clicks: number;
  };
  delivered_rate?: number;
  failed_rate?: number;
}

// A paginated section within OrgDetailResponse (transactions, campaigns) —
// the real API returns `pages`, not `total_pages`; both kept here as a
// guard, same as OrganizationsListResponse.
interface PaginatedSection<T> {
  page: number;
  limit: number;
  total: number;
  pages?: number;
  total_pages?: number;
  has_next?: boolean;
  has_prev?: boolean;
  data: T[];
}

export interface OrgDetailResponse {
  status: boolean;
  org: {
    id: string;
    name: string;
    type: string;
    is_active: boolean;
    domain_name: string | null;
    created_at: string;
    // CXO dashboard AI switch. Not confirmed to be in this response yet;
    // undefined means unknown.
    cxo_ai_enabled?: boolean;
  };
  wallet: {
    balance: number;
    total_inflow: number;
    last_transaction: OrgTransaction | null;
  };
  transactions: PaginatedSection<OrgTransaction>;
  campaigns?: PaginatedSection<OrgCampaign>;
}

// POST/GET/DELETE /admin/organizations/{org_id}/api-keys[/{key_id}]
export interface ApiKey {
  id: string;
  name: string;
  key_type: "live" | "test" | string;
  key_prefix: string;
  last_four: string;
  is_active: boolean;
  // Only ever present in the create response — the full key is never
  // returned again after that, so it must be shown/copied right away.
  plain_key?: string;
  created_at: string;
  last_used_at: string | null;
  doc_url?: string | null;
}

export interface ApiKeysListResponse {
  doc_url?: string | null;
  keys: ApiKey[];
}

export const formatOrgDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export const getOrgInitials = (name: string): string =>
  name
    ?.trim()
    ?.split(/\s+/)
    ?.slice(0, 2)
    ?.map((w) => w[0])
    ?.join("")
    ?.toUpperCase() || "?";

export const getOrgStatusStyle = (status?: string | boolean | null): string => {
  const normalized = typeof status === "boolean" ? (status ? "active" : "inactive") : (status || "").toLowerCase();
  switch (normalized) {
    case "active":
      return "bg-green-50 text-green-700 border border-green-100 dark:bg-green-500/15 dark:text-green-400 dark:border-green-500/30";
    case "inactive":
    case "disabled":
      return "bg-gray-100 text-gray-600 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    default:
      return "bg-amber-50 text-amber-700 border border-amber-100 dark:bg-amber-500/15 dark:text-amber-400 dark:border-amber-500/30";
  }
};
